import React, { useEffect, useMemo, useState, useRef } from "react";

// region Imports - External libraries
import _ from "lodash";
import * as dateFns from "date-fns";
import Loading from "react-fullscreen-loading";
// endregion Imports - External libraries
// region Imports - Shared
import { WaterAdditionTravelReport } from "@shared/interfaces/water-addition.interface";
import { IParamQueryValues } from "@shared/interfaces/filter.interface";
import { ParamNameTypes } from "@shared/constants/param-name-types.enum";
import { FilterApplicationTypesID } from "@shared/constants/filter-application-types.enum";
import { InternalCodeErrors } from "@shared/constants/internal-code-errors.enum";
import { Vehicle } from "@shared/interfaces/vehicle.interface";
import { Driver } from "@shared/interfaces/driver.interface";
import { Plant } from "@shared/entities/reg_plants.entity";
import { Regional } from "@shared/entities/reg_regionals.entity";
import { Group } from "@shared/entities/reg_groups.entity";
// endregion Imports - Shared
// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import {
  FilterMessages,
  MenuMessages,
  ToastMessages,
  WaterAdditionByTravelMessages
} from "@shared/languages/interfaces";
// endregion Imports - Languages
// region Imports - Services
import api from "@services/api";
// endregion Imports - Services
// region Imports - Utils
import utils from "@utils/useful-functions";
import { vehicleFilterLabel } from "@utils/vehicle-filter-label";
// endregion Imports - Utils
// region Imports - Hooks
import { useToast } from "@hooks/useToast";
// endregion Imports - Hooks
// region Imports - Organisms
import GenericQueryFilter, {
  IDateRangeOptions,
  IMultipleSelectionOptions,
  INumberRangeOptions,
  IOnFilterReturn
} from "@organisms/GenericQueryFilter";
// endregion Imports - Organisms

interface IProps {
  open: boolean;
  onClose: () => void;
  onFilter: (
    numberOfFilteredOptions: number,
    data: WaterAdditionTravelReport[],
    startDate: string,
    finishDate: string
  ) => void;
}

const QueryFilterWaterAdditionByTravel: React.FC<IProps> = ({ open, onClose, onFilter }) => {

  /* region Hooks */
  const { t } = useTranslation();
  const { addToast } = useToast();
  const paramsFilterRef = useRef<IParamQueryValues[]>([]);
  /* endregion Hooks */
  /* region States */
  const [loading, setLoading] = useState(false);
  const [optionsVehicleFilter, setOptionsVehicleFilter] = useState<Vehicle[]>([] as Array<Vehicle>);
  const [optionsDriverFilter, setOptionsDriverFilter] = useState<Driver[]>([] as Array<Driver>);
  const [optionsPlantFilter, setOptionsPlantFilter] = useState<Plant[]>([] as Array<Plant>);
  const [optionsRegionalFilter, setOptionsRegionalFilter] = useState<Regional[]>([] as Array<Regional>);
  const [optionsGroupFilter, setOptionsGroupFilter] = useState<Group[]>([] as Array<Group>);

  const [loadingVehicleFilter, setLoadingVehicleFilter] = useState(true);
  const [loadingDriverFilter, setLoadingDriverFilter] = useState(true);
  const [loadingPlantFilter, setLoadingPlantFilter] = useState(true);
  const [loadingRegionalFilter, setLoadingRegionalFilter] = useState(true);
  const [loadingGroupFilter, setLoadingGroupFilter] = useState(true);
  /* endregion States */
  /* region Constants */
  const paramNameStartDate = ParamNameTypes.START_DATE;
  const paramNameEndDate = ParamNameTypes.END_DATE;
  const paramNameVehicle = ParamNameTypes.ID_VEHICLES;
  const paramNameDriver = ParamNameTypes.ID_DRIVERS;
  const paramNamePlant = ParamNameTypes.ID_PLANTS;
  const paramNameRegional = ParamNameTypes.ID_REGIONALS;
  const paramNameGroup = ParamNameTypes.ID_GROUPS;
  const paramNameMinWater = ParamNameTypes.MIN_WATER_AMOUNT;
  const paramNameMaxWater = ParamNameTypes.MAX_WATER_AMOUNT;

  const defaultStartDate = dateFns.subDays(new Date(), 7);

  defaultStartDate.setHours(0, 0, 0, 0);

  const defaultEndDate = new Date();

  defaultEndDate.setHours(23, 59, 59, 999);

  const dateRangeOptions: IDateRangeOptions = {
    labelName: t(FilterMessages.dateRangeLabel),
    paramNameStartDate,
    paramNameEndDate,
    defaultValues: {
      startDate: utils.convertDateToISOWithTimezone(defaultStartDate),
      endDate: utils.convertDateToISOWithTimezone(defaultEndDate)
    }
    // maxRange removed - backend will validate and trigger scheduled report modal when needed
  };

  const vehicleFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingVehicleFilter,
    labelName: t(FilterMessages.optionVehicles),
    paramName: paramNameVehicle,
    values: optionsVehicleFilter.map((vehicle) => ({
      value: vehicle.id_vehicle,
      label: vehicleFilterLabel(vehicle)
    }))
  }), [loadingVehicleFilter, optionsVehicleFilter, t]);

  const driverFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingDriverFilter,
    labelName: t(MenuMessages.drivers),
    paramName: paramNameDriver,
    values: optionsDriverFilter.map((driver) => ({
      value: driver.id_driver!,
      label: driver.name
    }))
  }), [loadingDriverFilter, optionsDriverFilter, t]);

  const plantFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingPlantFilter,
    labelName: t(FilterMessages.optionPlants),
    paramName: paramNamePlant,
    values: optionsPlantFilter.map(
      (plant) => ({
        value: plant!.location!.id_location!,
        label: plant.name!
      })
    )
  }), [loadingPlantFilter, optionsPlantFilter, t]);

  const regionalFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingRegionalFilter,
    labelName: t(MenuMessages.regionals),
    paramName: paramNameRegional,
    values: optionsRegionalFilter.map((regional) => ({
      value: regional.id_regional!,
      label: `${regional.code} - ${regional.name}`
    }))
  }), [loadingRegionalFilter, optionsRegionalFilter, t]);

  const groupFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingGroupFilter,
    labelName: t(MenuMessages.groups),
    paramName: paramNameGroup,
    values: optionsGroupFilter.map((group) => ({
      value: group.id_group!,
      label: `${group.code} - ${group.name}`
    }))
  }), [loadingGroupFilter, optionsGroupFilter, t]);

  const waterQuantityOptions: INumberRangeOptions = {
    labelName: t(WaterAdditionByTravelMessages.waterTotal),
    paramNameStartValue: paramNameMinWater,
    paramNameEndValue: paramNameMaxWater
  };
  /* endregion Constants */
  /* region Functions */

  /**
   * Load vehicle options for the vehicle filter
   */
  const loadVehicleOptions = async () => {
    setLoadingVehicleFilter(true);
    try {
      const { data } = await api.get("vehicles/read", {
        params: { linkedHardware: true, onlyLabel: true }
      });

      if (data.status === "success") setOptionsVehicleFilter(data.result);
    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingVehicleFilter(false);
    }
  };

  /**
   * Load driver options for the driver filter
   */
  const loadDriverOptions = async () => {
    setLoadingDriverFilter(true);
    try {
      const { data } = await api.get("drivers/read");

      if (data.status === "success") setOptionsDriverFilter(data.result as Driver[]);
    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingDriverFilter(false);
    }
  };

  /**
   * Load plant options for the plant filter
   */
  const loadPlantOptions = async () => {
    setLoadingPlantFilter(true);
    try {
      const { data } = await api.get("plants/read", {
        params: { linkedLocation: true }
      });

      if (data.status === "success") setOptionsPlantFilter(data.result);
    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingPlantFilter(false);
    }
  };

  /**
   * Load regional options for the regional filter
   */
  const loadRegionalOptions = async () => {
    setLoadingRegionalFilter(true);
    try {
      const { data } = await api.get("regionals/read");

      if (data.status === "success") setOptionsRegionalFilter(data.result as Regional[]);
    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingRegionalFilter(false);
    }
  };

  /**
   * Load group options for the group filter
   */
  const loadGroupOptions = async () => {
    setLoadingGroupFilter(true);
    try {
      const { data } = await api.get("groups/read");

      if (data.status === "success") setOptionsGroupFilter(data.result as Group[]);
    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingGroupFilter(false);
    }
  };

  /**
   * Handle filter action
   * @param paramsData Parameters selected in the filter
   */
  const handleFilter = async (paramsData: IParamQueryValues[]): Promise<IOnFilterReturn> => {
    try {
      setLoading(true);

      paramsFilterRef.current = paramsData;

      const validParamsData = paramsData.filter((item) => !_.isEmpty(item.paramValue));

      const mappedParams = validParamsData.reduce((acc, param) => {
        acc[param.paramName] = param.paramValue;

        return acc;
      }, {} as { [key: string]: string | string[] });

      const requestData = {
        ...mappedParams,
        groupByTravel: true,
        includeVolume: true
      };

      const response = await api.post("/water-additions/get-by-travel", requestData);

      const validParamsDataWithoutDate = validParamsData.filter(
        (item) => item.paramName !== paramNameStartDate && item.paramName !== paramNameEndDate
      );

      if (response.data.status === "alert" && response.data.errorCode === InternalCodeErrors.VERY_LARGE_FILTER) {
        const additionalFilterOptions: IParamQueryValues[] = [
          { paramName: ParamNameTypes.UTC_OFFSET, paramValue: (utils.getUtcOffset()).toString() }
        ];

        return { status: "scheduledReport", additionalFilterOptions } as IOnFilterReturn;
      }

      if (response.data.status === "success") {
        const waterAdditions: WaterAdditionTravelReport[] = response.data.result || [];
        const startDate = mappedParams[paramNameStartDate] as string;
        const finishDate = mappedParams[paramNameEndDate] as string;

        onFilter(validParamsDataWithoutDate.length, waterAdditions, startDate, finishDate);

        if (waterAdditions.length === 0) {
          addToast({
            type: "info",
            title: t(ToastMessages.alert),
            description: t(WaterAdditionByTravelMessages.noDataFound)
          });
        }

        return { status: "success" };
      } else {
        addToast({
          type: "error",
          title: t(ToastMessages.error),
          description: response.data.message || t(ToastMessages.connectionNotEstablished)
        });

        return { status: "error" };
      }
    } catch (error) {
      utils.handleStandardError(error, t, addToast);

      return { status: "error" };
    } finally {
      setLoading(false);
    }
  };

  /* endregion Functions */
  /* region Effects */
  useEffect(() => {
    Promise.all([
      loadVehicleOptions(),
      loadDriverOptions(),
      loadPlantOptions(),
      loadRegionalOptions(),
      loadGroupOptions()
    ]).then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /* endregion Effects */

  return (
    <>
      <Loading loading={loading} />
      <GenericQueryFilter
        open={open}
        onClose={onClose}
        applicationTypeID={FilterApplicationTypesID.WATER_ADDITIONS_BY_TRAVEL}
        onFilter={handleFilter}
        dateRangeOptions={dateRangeOptions}
        multipleSelectionOptions={[
          vehicleFilterOptions,
          driverFilterOptions,
          plantFilterOptions,
          regionalFilterOptions,
          groupFilterOptions
        ]}
        numberRangeOptions={waterQuantityOptions}
      />
    </>
  );
};

export default QueryFilterWaterAdditionByTravel;
